import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Table } from "antd";
import ListTopSearch from "../../Global Functions/listTopSearch";
import { listResaleInterestedProject, updateStatusResaleInterestedProject } from "../../Redux/Actions/propertiesResaleActions";
import { columnsInterestedPropertyResale } from "../../Global Functions/columnsAntd";
import toast from "react-hot-toast";
import DateRangePopup from "../../Utils/dateFilterPopup";
import * as XLSX from "xlsx";

const ListInterestedPropertyResale = () => {
    const dispatch = useDispatch();

    const [searchQuery, setSearchQuery] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [isDatePopupVisible, setIsDatePopupVisible] = useState(false);
    const [dateRange, setDateRange] = useState({ start: null, end: null });

    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0,
        showSizeChanger: true,
        pageSizeOptions: ["5", "10", "20", "50", "100"],
    });

    const { admin } = useSelector((state) => state.admin || {});
    const accessCode = admin?.data?.access_code || [];
    const canEdit = Array.isArray(accessCode) && accessCode.includes("edit");

    const { resaleInterestedPropertyList, loading, listLoading } = useSelector((state) => state.propertiesResale || {});

    const fetchDetails = useCallback(async (page, limit) => {
        const projectData = {
            page,
            limit,
            is_deleted: false,
            filter: debouncedSearch,
            ...(dateRange.start && { start_date: dateRange.start.format("YYYY-MM-DD") }),
            ...(dateRange.end && { end_date: dateRange.end.format("YYYY-MM-DD") }),
        };

        try {
            await dispatch(listResaleInterestedProject(projectData));
        } catch {
            toast.error("Failed to fetch interested resale projects");
        }
    }, [dispatch, debouncedSearch, dateRange]);

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearch(searchQuery);
        }, 500);
        return () => clearTimeout(handler);
    }, [searchQuery]);

    useEffect(() => {
        fetchDetails(pagination.current, pagination.pageSize);
    }, [pagination.current, pagination.pageSize, debouncedSearch, dateRange, fetchDetails]);

    useEffect(() => {
        if (resaleInterestedPropertyList?.data?.count !== undefined) {
            setPagination((prev) => ({
                ...prev,
                total: resaleInterestedPropertyList?.data?.count || 0,
            }));
        }
    }, [resaleInterestedPropertyList]);

    const handleTableChange = (pagination) => {
        setPagination(pagination);
    };

    const handleDateRangeApply = (start, end) => {
        setDateRange(start === null && end === null ? { start: null, end: null } : { start, end });
        setIsDatePopupVisible(false);
        setPagination((prev) => ({ ...prev, current: 1 }));
    };

    const handleStatusUpdate = async (interested_property_id, status) => {
        try {
            const payload = {
                interested_property_id,
                status,
            };
            const response = await dispatch(updateStatusResaleInterestedProject(payload));
            if (response?.statusCode === 200) {
                await fetchDetails(pagination.current, pagination.pageSize);
                toast.success("Successfully update status");
            }
        } catch {
            toast.error("Failed to update status");
        }
    };

    const handleDownloadExcel = async () => {
        try {
            const projectData = {
                page: 1,
                limit: pagination.total || 1000,
                is_deleted: false,
                filter: searchQuery,
                ...(dateRange.start && { start_date: dateRange.start.format("YYYY-MM-DD") }),
                ...(dateRange.end && { end_date: dateRange.end.format("YYYY-MM-DD") }),
            };

            const response = await dispatch(listResaleInterestedProject(projectData));
            const rawData = response?.data?.data || resaleInterestedPropertyList?.data?.data || [];

            if (!Array.isArray(rawData) || rawData.length === 0) {
                toast.error("No data available to download");
                return;
            }

            const preparedData = rawData.map((item) => ({
                project_id: item?.project?.project_id || "",
                project_name: item?.project?.project_name || "",
                user_id: item?.user?.user_id || "",
                user_name: item?.user?.name || "",
                email: item?.user?.email || "",
                contact_number: item?.user?.contact_number || "",
                status: item?.status || "",
                created_at: item?.created_at || "",
            }));

            const worksheet = XLSX.utils.json_to_sheet(preparedData);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, "InterestedProjectResale");
            XLSX.writeFile(workbook, "interested_project_resale_list.xlsx");
            toast.success("Download started!");
        } catch {
            toast.error("Failed to download data");
        }
    };

    return (
        <>
            <div className="bookings_table">
                <div className="table_component_all">
                    <div style={{ gridColumn: "span 5", height: "auto" }}>
                        <ListTopSearch
                            h3heading="Interested Project Resale List"
                            setState={setSearchQuery}
                            onCalendarClick={() => setIsDatePopupVisible(true)}
                            onDownloadProject={canEdit ? handleDownloadExcel : undefined}
                        />
                    </div>

                    <div className="custom_table">
                        <div style={{ width: "90%" }}>
                            <Table
                                dataSource={(Array.isArray(resaleInterestedPropertyList?.data?.data) && resaleInterestedPropertyList?.data?.data) || []}
                                virtual
                                scroll={{ x: 1700, y: 500 }}
                                pagination={pagination}
                                onChange={handleTableChange}
                                loading={listLoading || loading}
                                columns={columnsInterestedPropertyResale(handleStatusUpdate, canEdit)}
                                rowClassName={() => "text-center"}
                                rowKey="_id"
                            />
                        </div>
                    </div>
                </div>
            </div>

            <DateRangePopup
                visible={isDatePopupVisible}
                initialStart={dateRange.start}
                initialEnd={dateRange.end}
                onCancel={() => setIsDatePopupVisible(false)}
                onOk={handleDateRangeApply}
            />
        </>
    );
};

export default ListInterestedPropertyResale;